// Chord detection and harmonic HUD
import { COLORS, INTERVALS, NOTE_NAMES, INTERVAL_LABELS } from './data';
import type { ChordInfo, IntervalData } from './types';

export function noteName(midi: number): string {
  return COLORS[midi]?.[0] || `${NOTE_NAMES[midi % 12]}${Math.floor(midi / 12) - 1}`;
}

export function midiFrequency(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

export function intervalName(semitones: number): string {
  const normalized = semitones % 12;
  if (semitones !== 0 && normalized === 0) return `${semitones / 12} Oct${semitones >= 24 ? 's' : ''}`;
  return INTERVAL_LABELS[normalized] || `${semitones}st`;
}

export function intervalShapeFor(normalized: number): IntervalData | null {
  const nd = normalized === 0 ? 12 : normalized;
  for (const v of Object.values(INTERVALS)) {
    if (v.semis.includes(nd)) return v;
  }
  return null;
}

export function classifyChord(sorted: number[]): ChordInfo {
  if (sorted.length === 0) {
    return { root: '—', quality: '—', symbol: 'Resting Harmonics', notes: '—', intervals: '—', shape: null, blend: 0.45 };
  }

  const root = sorted[0];
  const pcs = new Set(sorted.map(m => ((m - root) % 12 + 12) % 12));
  const has = (n: number) => pcs.has(n);

  let quality = 'Open Voicing';
  let suffix = '';

  if (has(4) && has(7)) { quality = 'Major'; suffix = 'maj'; }
  else if (has(3) && has(7)) { quality = 'Minor'; suffix = 'min'; }
  else if (has(3) && has(6)) { quality = 'Diminished'; suffix = 'dim'; }
  else if (has(4) && has(8)) { quality = 'Augmented'; suffix = 'aug'; }
  else if (has(5) && has(7)) { quality = 'Suspended Fourth'; suffix = 'sus4'; }
  else if (has(2) && has(7)) { quality = 'Suspended Second'; suffix = 'sus2'; }
  else if (sorted.length === 1) { quality = 'Single Pitch'; suffix = ''; }

  if (sorted.length > 2) {
    if (has(10)) { quality += ' Seventh'; suffix += '7'; }
    else if (has(11)) { quality += ' Major Seventh'; suffix += 'maj7'; }
    if (has(2) && !quality.includes('Suspended')) { quality += ' add9'; suffix += ' add9'; }
    if (has(9)) { quality += ' add13'; suffix += ' add13'; }
  }

  const intervals = sorted.map((m, i) => i === 0 ? 'Root' : intervalName(m - root));

  let shape: IntervalData | null = null;
  for (const m of sorted.slice(1)) {
    shape = intervalShapeFor((m - root) % 12);
    if (shape) break;
  }

  return {
    root: `${noteName(root)} · ${midiFrequency(root).toFixed(1)} Hz`,
    quality,
    symbol: sorted.length === 1 ? `Pitch — ${noteName(root)}` : `${noteName(root)} ${suffix}`.trim(),
    notes: sorted.map(noteName).join(' · '),
    intervals: intervals.join(' · '),
    shape,
    blend: shape?.blend ?? 0.5
  };
}

export function updateHarmonicHud(info: ChordInfo) {
  const rootEl = document.getElementById('harmonicRoot');
  const qualityEl = document.getElementById('harmonicQuality');
  const notesEl = document.getElementById('harmonicNotes');
  const intervalsEl = document.getElementById('harmonicIntervals');
  if (rootEl) rootEl.textContent = info.root;
  if (qualityEl) qualityEl.textContent = info.quality;
  if (notesEl) notesEl.textContent = info.notes;
  if (intervalsEl) intervalsEl.textContent = info.intervals;
}